/**
 * API client for one call and the call log it lands in: placing it, holding it,
 * hanging it up, and everything the history shows afterwards.
 *
 * The account settings behind all of this live in `lib/telephony.ts` and the
 * Twilio-side recording storage in `lib/callRecordings.ts`; both are re-exported
 * below so callers keep importing from `lib/calls` (AGENTS.md §4). Every
 * endpoint is platform-scoped, behind one of the three telephony keys since the
 * §3.8 split: `use:crm.calls` to be on the phone and read one's own calls,
 * `read:crm.calls.recordings` to open somebody else's, `manage:crm.calls` for
 * the desk's own configuration (which is what `lib/telephony.ts` talks to).
 */
import { api } from "./api";
import type { NoticeLocale } from "./telephonyNotice";
import type {
  BridgeMode,
  CallDetail,
  CallDirection,
  CallNextStep,
  CallRow,
  CallSource,
  CallStepTask,
  ServerCallSource,
} from "./callTypes";

// The door stays where it always was: this file was split for size, not to move
// anybody's import.
export * from "./telephony";
export * from "./callRecordings";
export * from "./callTypes";

// ── Calling ─────────────────────────────────────────────────────────────────

/**
 * The other person's number, whichever column holds them.
 *
 * The API keeps `toNumber`/`fromNumber` meaning exactly what they say, so every
 * screen that wants "who was on this call" asks here instead of picking a column
 * and being right half the time. Mirrors `counterpartNumber` in the API's
 * `crm-calls/lib/direction.ts` - the same rule, on the two sides of the wire.
 *
 * Empty is a real answer, not a bug: Twilio withholds `From` on a blocked caller
 * id, so an inbound row can genuinely have nobody to name or dial back. Callers
 * that turn this into an action have to check it - see the call-back control on
 * `CallPane`, which hides itself rather than dialling "".
 */
export function callCounterpart(row: {
  direction?: CallDirection;
  toNumber: string;
  fromNumber?: string | null;
}): string {
  return row.direction === "inbound" ? (row.fromNumber ?? "").trim() : row.toNumber;
}

/**
 * OUR number on this call - the line it went out on, or the one they rang.
 *
 * The exact inverse of `callCounterpart`, and it exists so that neither of the
 * two questions ("who was that?" and "which of our numbers was this?") is ever
 * answered by reaching for a column directly. Mirrors `ownNumber` in the API's
 * `crm-calls/lib/direction.ts`.
 */
export function callOwnLine(row: {
  direction?: CallDirection;
  toNumber: string;
  fromNumber?: string | null;
}): string {
  return row.direction === "inbound" ? row.toNumber : (row.fromNumber ?? "").trim();
}

/**
 * Is this a call somebody owes a call back?
 *
 * Mirrors `canHandle` in the API's `crm-calls/lib/callback.ts`, and is used only
 * to decide whether to DRAW the tick - the server refuses the write on anything
 * else, so a drift between the two costs a menu entry that 409s rather than a
 * mark that goes nowhere. Deliberately says nothing about `handledAt`: a call
 * that has been ticked has to stay untickable-back.
 *
 * The voicemail clause is not redundant. A message left on a line that was
 * diverted and answered by somebody's personal mailbox is `completed`, and that
 * is exactly the call that needs returning.
 */
export function isCallback(row: {
  direction?: CallDirection;
  status: string;
  voicemail: boolean;
}): boolean {
  if (row.direction !== "inbound") return false;
  return row.voicemail || row.status === "no-answer" || row.status === "busy";
}

export interface CreateCallInput {
  toNumber: string;
  /**
   * Which of our own lines to show the far end, when the operator picked one.
   *
   * Omitted takes the account caller id. A number this Twilio account may not
   * present comes back 422 `unknown_caller_id` rather than being quietly
   * swapped for the default - a silent swap is the bug this field exists to
   * fix, and repeating it one layer down would be worse.
   */
  fromNumber?: string;
  companyName?: string;
  regCode?: string;
  leadDomain?: string;
  /**
   * The CRM record the operator clicked from, if the click happened on one.
   *
   * Sent at dial time so the call is filed against the right party from the
   * start. A call placed from the dial pad has neither, and the call pane's
   * party picker is how it gets one afterwards.
   */
  clientId?: string;
  leadId?: string;
  /**
   * The PERSON whose own number was clicked - a contact row, not a company one.
   *
   * Sent instead of `clientId`, not beside it: the server takes the company
   * from the contact itself, so the two cannot end up naming different
   * companies on one row. A `clientId` sent as well is overruled, not refused.
   */
  contactId?: string;
  source: CallSource;
  /**
   * This call's own answer on recording and the after-call transcript. Omit
   * either one to take the account setting (CRM Settings -> Telephony), which is
   * what every caller outside the dial pad does.
   *
   * The server clamps them: nothing is transcribed that was not recorded, and
   * nothing is transcribed at all without a Gemini key.
   */
  record?: boolean;
  transcribe?: boolean;
  /**
   * Play the spoken announcement at the start of THIS call. Can only be turned
   * off: a call cannot announce something the account has no wording for.
   */
  notice?: boolean;
  /**
   * Which language to announce in. Omitted is the account's base wording, which
   * is what every caller outside the dial pad sends and what every call placed
   * before this existed means.
   */
  noticeLocale?: NoticeLocale;
  /**
   * Ring the operator on their own mobile rather than in this browser.
   *
   * The one field here that changes WHO is on the call rather than what the
   * call does, and the only one that changes what the browser does next: on a
   * `true` there is no Voice SDK Device to connect, because the server has
   * already placed the operator's leg by the time this resolves.
   */
  viaMobile?: boolean;
}

/**
 * Where the operator's own leg is: in this browser, or on their telephone.
 *
 * The window reads it to decide whether to wait for a `Call` object at all, and
 * which controls to take away - mute, the keypad and the audio routing are all
 * operations on a `Call` that does not exist on a mobile leg.
 */
export type OperatorLeg = "browser" | "mobile";

/** What the create endpoint pinned onto the row - the call's real answer. */
export interface CreatedCall {
  id: string;
  toNumber: string;
  fromNumber: string | null;
  recordEnabled: boolean;
  transcribeEnabled: boolean;
  noticeEnabled: boolean;
  /** The language pinned on the row. `null` is the account's base wording. */
  noticeLocale: string | null;
  /**
   * How this call is about to be bridged, so the Hold button renders in its
   * real state on the first paint instead of after the first poll. Computed
   * from the account setting rather than read off the row: the column is
   * pinned by the voice webhook a moment later, from the same configuration.
   */
  bridgeMode: BridgeMode;
  /**
   * Which leg the operator is on, as the server placed it.
   *
   * Read rather than assumed from what was asked for: `viaMobile` is a request
   * and this is the answer, and the two differ whenever the server refused -
   * which it does by failing the request outright, so a `201` saying "browser"
   * to a pad that asked for the mobile would be a bug worth seeing rather than
   * a state worth guessing around.
   */
  operatorLeg: OperatorLeg;
}

/** The operator's own mobile, for "call from my mobile". "" when unset. */
export function getMyMobile(): Promise<{ mobileNumber: string }> {
  return api.get("/admin/crm/calls/my-mobile");
}

/** Save it, or clear it with "". Rejects with `invalid_number` on a typo. */
export function saveMyMobile(mobileNumber: string): Promise<{ mobileNumber: string }> {
  return api.put("/admin/crm/calls/my-mobile", { mobileNumber });
}

export interface VoiceToken {
  token: string;
  identity: string;
  ttl: number;
  edge: string;
  /**
   * The token may receive calls. Decides whether the Device registers at all -
   * an installation that does not answer its phone should not hold a websocket
   * open for every operator with the CRM on screen.
   */
  incoming: boolean;
  /**
   * The operator's own ringtone, or "" for the trill the front end ships with.
   *
   * It travels with the token rather than on a settings endpoint of its own
   * because it is needed at the moment a call arrives, and one more round trip
   * at that moment would be a phone that rings late or not at all.
   */
  ringtoneUrl: string;
  /**
   * The operator's own OUTGOING ring, or "" for the Voice SDK's own sound.
   *
   * Empty means something different here than it does above: there is no
   * shipped default for this one, so "" leaves the SDK's sound switched on
   * rather than falling back to a file of ours.
   */
  ringbackUrl: string;
}

export function createCallLog(input: CreateCallInput): Promise<CreatedCall> {
  return api.post("/admin/crm/calls", input);
}

export function getVoiceToken(): Promise<VoiceToken> {
  return api.post("/admin/crm/calls/token", {});
}

/**
 * How far a window has got with Twilio, as the lease reads it.
 *
 * Three values rather than a boolean: a window that is merely standing by has
 * not tried to register and is a perfectly good candidate for the phone, while
 * one on the retry ladder has tried and been refused. The server treats those
 * two as opposites.
 */
export type CallPresenceReach = "ready" | "trying" | "failed";

/** One window's bid for the telephone, sent on a heartbeat. */
export interface CallPresenceBid {
  windowId: string;
  /** When this window last became the active one. An event, never a poll. */
  claimedAt: number;
  reach: CallPresenceReach;
  busy: boolean;
}

export interface CallPresenceAnswer {
  /** Does this window hold the telephone across every machine, not just this browser? */
  mine: boolean;
  /** The holding window's id. Somebody always holds it after a beat. */
  holder: string;
  /** Epoch ms of the holder's last beat, for wording like "in use a minute ago". */
  holderAt: number;
  /**
   * How long to wait before beating again.
   *
   * Served rather than hard-coded here, so the interval and the timeout that
   * judges it cannot drift apart across a deploy. A client beating slower than
   * the server expects loses the phone to another machine by itself.
   */
  beatMs: number;
}

export function beatCallPresence(bid: CallPresenceBid): Promise<CallPresenceAnswer> {
  return api.post("/admin/crm/calls/presence", bid);
}

export function releaseCallPresence(windowId: string): Promise<void> {
  return api.post("/admin/crm/calls/presence/release", { windowId });
}

/** Where an inbound number was recognised, most specific source first. */
export type PhoneMatchKind = "client" | "contact" | "lead" | "register";

export interface PhoneMatch {
  kind: PhoneMatchKind;
  id: string;
  name: string;
  regCode: string | null;
  clientId: string | null;
}

/**
 * Who does this number belong to? Every match, not the best one - a number
 * shared by three companies is a list the operator reads while the phone rings.
 */
export function lookupPhone(number: string): Promise<{ number: string; matches: PhoneMatch[] }> {
  return api.get(`/admin/crm/phone-lookup?number=${encodeURIComponent(number)}`);
}

/**
 * Take ownership of an inbound call. Stamps the operator onto the row, which is
 * what the co-pilot's ownership check reads - so this must land BEFORE the tap
 * starts, or the first window comes back forbidden.
 *
 * `legSid` only on a PARKED call, where the claim is also the decision about
 * which of two ringing operators gets the caller. The server answers 409 when
 * it is not this one, and the browser must not answer the offer then.
 */
export function claimCall(id: string, legSid?: string): Promise<CallDetail> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/claim`, legSid ? { legSid } : {});
}

/** The row behind a parked call's offer - see `findParkedOffer`. */
export interface ParkedOffer {
  callLogId: string;
  /** The caller's number as the row has it, "" when they withheld it. */
  from: string;
  intentDepartment: string;
  noticeLocale: string;
}

/**
 * The row behind a PARKED call's offer, found by the offer's own leg sid.
 *
 * A parked caller's operators are rung into a room by the REST API, and that
 * offer carries no `<Parameter>` - so the row id the `<Dial>` path hands the
 * card is not on it. A 404 is normal for a moment: the offer can arrive before
 * the server has written its legs down. See `telephony/parkedOffer.ts`.
 */
export function findParkedOffer(legSid: string): Promise<ParkedOffer> {
  return api.get(`/admin/crm/calls/by-leg/${encodeURIComponent(legSid)}`);
}

// ── The conference bridge (hold) ────────────────────────────────────────────

export interface CallBridgeState {
  status: string;
  bridgeMode: BridgeMode;
  hold: boolean;
  holdChangedAt: string | null;
  /**
   * The customer has actually ENTERED THE ROOM, which is later than having
   * picked up: the announcement plays to them first. This is what promotes the
   * call window to "connected" now that `answerOnBridge` has no equivalent.
   */
  bridged: boolean;
  /**
   * Is the call really being recorded? On a conference the recording is a
   * separate REST call that can fail on its own, so the red dot has to follow
   * what happened rather than what was promised.
   */
  recording: boolean;
  /** Why the recording cannot be stopped by hand, or "" when it can. */
  recordingStopBlocked: RecordingStopBlock;
  /**
   * Twilio's own talk time, once the call is over. Null while it is running,
   * and null on a call that was never answered.
   */
  durationSec: number | null;
  /** The third party on this call right now, or null when there is nobody. */
  transfer: CallTransferState | null;
  /** Why nobody can be invited, or "" when they can. See `TransferBlock`. */
  transferBlocked: TransferBlock;
}

// ── A third party on the call ───────────────────────────────────────────────

/**
 * What the operator asked for, and these are not two labels.
 *
 * "add" puts a third voice in the room and the operator stays. "handover" is
 * the operator leaving: the server clears their own leg's `endConferenceOnExit`
 * before the third party is dialled, so the room outlives them - which also
 * means the red button stops meaning "end this call" and starts meaning "leave
 * it". The window says so; see `LiveCall`.
 */
export type TransferMode = "add" | "handover";

/**
 * Why a third party cannot be invited, or "" when one can.
 *
 * Decided by the server because it rests on things the window is deliberately
 * never told - the room's sid, the customer's leg. Same shape as the hold
 * button's own reasons: a disabled control with no explanation is the same as a
 * broken one.
 */
export type TransferBlock = "" | "not_conference" | "not_live" | "not_bridged" | "in_progress";

export interface CallTransferState {
  number: string;
  mode: TransferMode;
  /** Their leg's own status, or "" before the first callback lands. */
  status: string;
  /** They are in the room and can be heard - later than their phone being picked up. */
  joined: boolean;
}

/**
 * Ring somebody into a call that is already up.
 *
 * The number goes through the same three gates a dialled number does - the
 * block list, the shape, and Twilio Lookup where it is switched on - so a
 * transfer cannot reach a number the account has promised not to call.
 */
export function transferCall(
  id: string,
  to: string,
  mode: TransferMode,
): Promise<{ transfer: CallTransferState | null }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/transfer`, { to, mode });
}

/**
 * Take the third party off again, whether they answered or not.
 *
 * One call for both, because they are one intention: a leg still ringing is
 * cancelled and a leg in the room is completed. Calling off a handover also
 * puts the operator's own leg back in charge of the room.
 */
export function cancelCallTransfer(id: string): Promise<{ transfer: null }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/transfer/cancel`, {});
}

/**
 * Hold the customer, or bring them back.
 *
 * NOT `/live` - that path belongs to the co-pilot. See bridge.routes.ts.
 */
export function setCallHold(id: string, hold: boolean): Promise<{ hold: boolean }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/hold`, { hold });
}

export function getCallBridge(id: string): Promise<CallBridgeState> {
  return api.get(`/admin/crm/calls/${encodeURIComponent(id)}/bridge`);
}

// ── Stopping the recording mid-call ─────────────────────────────────────────

/**
 * Who asked for the recording to stop.
 *
 * Two codes rather than a free-text box, because this is the one number that
 * says whether the announcement's promise is being kept: the announcement tells
 * the far end to speak up if they would rather the call were not recorded, and
 * "how often does somebody take us up on that" cannot be counted out of prose.
 * Anything the operator wants to say about it goes in the note beside the
 * button.
 */
export type RecordingStopReason = "objection" | "operator";

/**
 * Why the recording cannot be stopped, or "" when it can.
 *
 * Server-decided for the same reason `TransferBlock` is - it rests on the call's
 * pinned flags and its Twilio leg, neither of which the window is told about.
 *
 * `already_stopped` exists as its own answer rather than collapsing into
 * `not_recording`: the stop turns this call's recording flag off, so every check
 * afterwards would truthfully say "not being recorded" - which reads, to the
 * operator who pressed the button ten seconds ago, as the button having done
 * nothing.
 */
export type RecordingStopBlock =
  | ""
  | "already_stopped"
  | "not_live"
  | "not_recording"
  | "no_leg"
  // A call the AI placed, before a person's half of it records: the dot there
  // is the AI's own tape, which the stop does not reach (ring R6c).
  | "takeover_not_bridged";

export interface RecordingStopResult {
  /** Twilio has stopped recording. The audio is no longer growing. */
  stopped: boolean;
  /**
   * Twilio's copy of the partial audio is gone as well.
   *
   * `false` does NOT undo the stop: the recording has finished, and our own
   * recording callback refuses to download it whatever happens. What is left is
   * a file on the Twilio account that the orphan report can clear. Reported
   * separately so the window can say what it actually knows.
   */
  destroyed: boolean;
  reason: RecordingStopReason | "already_stopped";
}

/**
 * Stop recording this call, now, without ending it.
 *
 * The announcement says "if you would rather we did not, please say so now".
 * Until this existed there was nothing to press when somebody did, and the only
 * way to honour it was to hang up and ring back - which is incomprehensible to
 * the person on the phone and in practice meant the promise was not kept.
 *
 * Stopping also takes the live co-pilot off the call. Silencing the recorder and
 * carrying on uploading the same voice to a model would be the same dishonesty
 * with an extra step.
 */
export function stopCallRecording(
  id: string,
  reason: RecordingStopReason,
): Promise<RecordingStopResult> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/recording/stop`, { reason });
}

/**
 * Fetch a recording our side gave up on.
 *
 * The download stops being retried after a day and the row goes to `failed`,
 * which is permanent - but Twilio keeps the file, so "failed" almost always means
 * our request was wrong rather than the audio being gone. It happened here: a
 * recording made in the ie1 region was fetched from `api.twilio.com` and answered
 * 404, which reads exactly like "not ready yet". Every one of those rows stayed
 * abandoned after the fix, because nothing could ask again.
 *
 * Refuses on a call whose audio was deliberately removed. A retry button that
 * could undo somebody's objection would be worse than the bug it fixes.
 */
export function refetchCallRecording(
  id: string,
): Promise<{ status: string; fetched: boolean }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/recording/refetch`, {});
}

/**
 * Hanging up before the customer ever joined. Best effort: the server's own
 * `participant-leave` handler is the authority, and this only closes the window
 * where that webhook is lost and a stranger's phone keeps ringing.
 */
export function abandonBridge(id: string): Promise<{ ended: boolean }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/bridge/end`, {});
}

/**
 * End the call through the server rather than through the Voice SDK.
 *
 * The SDK's own `disconnect()` is a message over a signalling websocket, and
 * that websocket is exactly what stops working when the access token lapses:
 * Twilio answers the reconnect with 20101 and every send after it with 31009,
 * while the audio - which is WebRTC and nothing to do with signalling - carries
 * on. The Hang up button therefore does both, and this is the half that does
 * not depend on the browser still being on speaking terms with Twilio.
 *
 * `ended: false` means there was nothing left to end, which is a normal answer.
 */
export function hangupCallServerSide(id: string): Promise<{ ended: boolean; error?: string }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/hangup`, {});
}


export interface CallListParams {
  q?: string;
  status?: string;
  transcriptStatus?: string;
  /**
   * The "Recorded" folder, answered by the SERVER.
   *
   * It used to be a `.filter` over the rows the browser was already holding,
   * which meant the folder showed the recorded calls among the newest hundred
   * and presented that as the answer.
   */
  recordingStatus?: string;
  regCode?: string;
  /**
   * Every call with one party, for that party's own page. A converted lead's
   * calls answer to both keys at once, so ask the one the page is about.
   */
  clientId?: string;
  leadId?: string;
  /** Which way the call went. */
  direction?: CallDirection;
  /** Only calls carrying a message. Not the same as "unanswered". */
  voicemail?: boolean;
  /**
   * The callback queue: inbound calls nobody has dealt with yet.
   *
   * One switch rather than a direction plus a status plus a flag, because the
   * server's predicate has an OR in it these three cannot express - and because
   * the number on the folder and the folder itself have to be one question.
   *
   * `true` only, never `false`, for the same reason `deep` below is: the server
   * coerces the string "false" to true and `listCalls` serializes everything
   * that is defined.
   */
  needsCallback?: true;
  /**
   * Whose calls: `"me"`, `"none"`, or one operator's user id.
   *
   * `"me"` is deliberately a WORD rather than the viewer's id. The server reads
   * it off the session, so the "My calls" folder stays a link that means "mine"
   * to whoever opens it instead of one that shows the sender's calls after it
   * has been pasted into a colleague's chat. `"none"` is the unclaimed inbound
   * rows, which carry an empty operator and cannot be asked for with a blank.
   */
  operator?: string;
  /** Where the number was picked up, plus "inbound" and "ai-outbound" for calls no person placed. */
  source?: CallSource | ServerCallSource;
  /** Talk-time floor in seconds; above zero this necessarily drops the
   *  unanswered calls, which have no duration to compare. */
  minSec?: number;
  /** One operator label id. Absent means every call, whatever it is labelled. */
  tag?: string;
  /** What was really announced: "played" | "silent" | "waived". Evidence, not intent. */
  notice?: "played" | "silent" | "waived";
  /**
   * Search the transcript, the summary and the note as well as the row.
   *
   * OMIT it when off rather than sending false: the server coerces the string
   * "false" to true, and everything defined here is serialized.
   */
  deep?: true;
  page?: number;
  pageSize?: number;
}

export function listCalls(params: CallListParams = {}): Promise<{ total: number; page: number; pageSize: number; rows: CallRow[] }> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api.get(`/admin/crm/calls${suffix}`);
}

export function getCall(id: string): Promise<CallDetail> {
  return api.get(`/admin/crm/calls/${encodeURIComponent(id)}`);
}

export function retryTranscription(id: string): Promise<{ ok: boolean }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/transcribe`, {});
}

/** Where a call's audio was removed from, per store. */
export interface EraseResult {
  ok: boolean;
  storage: "deleted" | "none" | "failed";
  twilio: "deleted" | "none" | "failed";
}

/**
 * Delete this call's audio. The row, note, summary and labels stay.
 *
 * The ordinary answer to "please delete that recording", which is almost never a
 * request to forget the call happened. `confirm` is in the body rather than
 * implied by the method: nothing brings the audio back.
 */
export function eraseCallRecording(id: string): Promise<EraseResult> {
  return api.del(`/admin/crm/calls/${encodeURIComponent(id)}/recording`, { confirm: true });
}

/** Delete the whole call. A tombstone stays behind so the erasure is provable. */
export function eraseCall(id: string, reason?: string): Promise<EraseResult> {
  return api.del(`/admin/crm/calls/${encodeURIComponent(id)}`, { confirm: true, reason });
}

/**
 * The fields an operator owns on a call.
 *
 * Omit a key to leave it alone, pass `null` to clear it - so "detach the client"
 * and "keep the client, replace the note" are the same call with different keys.
 * `tags` is the whole label set rather than a delta, and an empty array is how a
 * call is cleared of labels, so it has no null.
 *
 * `nextSteps` looks like the same whole-list idiom but is not: the server reads
 * only the `done` flags off it and matches them to its own steps by text. The
 * text travels so the match can be made, never so it can be written.
 */
export function patchCall(
  id: string,
  body: {
    clientId?: string | null;
    leadId?: string | null;
    /** The person on the call. Setting one also moves `clientId` to that
     *  person's own company; moving the company to another one clears this. */
    contactId?: string | null;
    note?: string | null;
    tags?: string[];
    nextSteps?: CallNextStep[];
    /** An operator correcting what the model wrote. Refused with 403 unless the
     *  caller may HEAR this call - see `lib/transcript-edit.ts` on the server.
     *  A blank string is stored as null, not as the machine's "unreadable". */
    transcriptText?: string | null;
    transcriptSummary?: string | null;
  },
): Promise<CallDetail> {
  return api.patch(`/admin/crm/calls/${encodeURIComponent(id)}`, body);
}

/**
 * File one next step as a CRM task.
 *
 * Needs `manage:crm.projects` on top of the calls permission, because it creates
 * a task and that is what creating a task costs everywhere else. 409 means the
 * step already has one, which is what a double click looks like from here.
 */
export function createStepTask(
  id: string,
  body: { text: string; dueDate?: string | null; priority?: "low" | "normal" | "high" | "urgent" },
): Promise<{ task: CallStepTask; steps: CallNextStep[] }> {
  return api.post(`/admin/crm/calls/${encodeURIComponent(id)}/steps/task`, body);
}

/** Same-origin and cookie-authed, so it works straight as `<audio src>` or a
 *  download href - no signed URL involved. */
export function callAudioUrl(id: string): string {
  return `/api/admin/crm/calls/${encodeURIComponent(id)}/audio`;
}

/**
 * The current list, as a spreadsheet. Same-origin and cookie-authed like
 * `callAudioUrl`, so it works as a plain `<a href download>`.
 *
 * Takes the SAME params object the list was fetched with, and that is the whole
 * design: given the same value, the file and the screen ask the server the same
 * question. Two keys are dropped on the way out and both drops are deliberate -
 * `page`/`pageSize`, because a file is not paged, and `q`/`deep`, which the
 * export endpoint does not accept at all (a deep search over 20000 rows is a
 * sequential scan; see the route's own note).
 *
 * The same `URLSearchParams` loop as `listCalls`, including the skip on `""`.
 * Callers must OMIT the boolean filters when they are off rather than send
 * false: the server coerces the STRING "false" to true.
 */
export interface CallExportParams extends CallListParams {
  /**
   * The one filter the list does NOT send to the server.
   *
   * The "Recordings" folder sifts its page in the browser, so an export built
   * from the list's parameters alone would return every call rather than the
   * recorded ones. The export endpoint accepts this; the list endpoint has no
   * use for it.
   */
  recordingStatus?: string;
}

export function callsCsvUrl(params: CallExportParams = {}): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (k === "page" || k === "pageSize" || k === "q" || k === "deep") continue;
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return `/api/admin/crm/calls/export.csv${suffix}`;
}

